/**
 * iter150AZ.71 — Outbound IP Whitelist (super-admin only).
 *
 * Shows the IPs this backend uses for outbound traffic (auto-detected
 * by hitting api.ipify.org / ifconfig.me / icanhazip / ipinfo) plus
 * any manually-added extras a super-admin has saved. Below the IPs is
 * a catalog of external services where each IP must be whitelisted
 * (Bluehost MariaDB, Twilio, SendGrid, etc.).
 *
 * Backed by:
 *   GET    /api/admin/egress-ips
 *   POST   /api/admin/egress-ips/refresh
 *   POST   /api/admin/egress-ips/extras   { ip, label }
 *   DELETE /api/admin/egress-ips/extras   { ip }
 *
 * URL preserved at /admin/whitelist-bootstrap (back-compat with the
 * TopBar menu link from the previous iteration).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Copy, ArrowsClockwise, Plus, Trash, ArrowSquareOut, Warning,
  CheckCircle, Globe, Lightning, Info, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function _authHeaders() {
  try {
    const saved = localStorage.getItem("usair_user");
    const email = saved ? JSON.parse(saved)?.email : "";
    return email ? { "X-User-Email": email } : {};
  } catch {
    return {};
  }
}

async function _readError(resp) {
  try {
    const j = await resp.json();
    return j?.detail || resp.statusText || "Unknown error";
  } catch {
    return resp.statusText || "Unknown error";
  }
}

function _humanAge(seconds) {
  if (seconds === undefined || seconds === null) return "—";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

export default function WhitelistBootstrapPage({ user, onLogout, onLogoutAll }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // Add-extra form state
  const [addIp, setAddIp] = useState("");
  const [addLabel, setAddLabel] = useState("");
  const [adding, setAdding] = useState(false);

  // Verify-service state — keyed by service.key:
  //   { running: bool, result: {ok, status, latency_ms, error}|null }
  const [verifyState, setVerifyState] = useState({});

  const verifyService = useCallback(async (svcKey) => {
    setVerifyState((prev) => ({
      ...prev,
      [svcKey]: { running: true, result: null },
    }));
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/egress-ips/verify/${encodeURIComponent(svcKey)}`,
        { method: "POST", credentials: "include", headers: _authHeaders() },
      );
      if (!resp.ok) throw new Error(await _readError(resp));
      const data = await resp.json();
      setVerifyState((prev) => ({
        ...prev,
        [svcKey]: { running: false, result: data },
      }));
      if (data.ok) {
        toast.success(`${data.service_name}: ${data.status} (${data.latency_ms} ms)`);
      } else {
        toast.error(`${data.service_name}: ${data.error || "unreachable"}`);
      }
    } catch (e) {
      setVerifyState((prev) => ({
        ...prev,
        [svcKey]: { running: false, result: { ok: false, error: e.message || "Request failed" } },
      }));
      toast.error(e.message || "Verification request failed");
    }
  }, []);

  const fetchPayload = useCallback(async (opts = {}) => {
    const { force = false, swallowError = false } = opts;
    if (force) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const url = force
        ? `${BACKEND_URL}/api/admin/egress-ips/refresh`
        : `${BACKEND_URL}/api/admin/egress-ips`;
      const resp = await fetch(url, {
        method: force ? "POST" : "GET",
        credentials: "include",
        headers: _authHeaders(),
      });
      if (!resp.ok) throw new Error(await _readError(resp));
      const data = await resp.json();
      setPayload(data);
    } catch (e) {
      if (!swallowError) setError(e.message || "Failed to load egress IPs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchPayload(); }, [fetchPayload]);

  const addExtra = async (e) => {
    e?.preventDefault?.();
    if (!addIp.trim()) {
      toast.error("Enter an IP address.");
      return;
    }
    setAdding(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/egress-ips/extras`, {
        method: "POST",
        credentials: "include",
        headers: { ..._authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ip: addIp.trim(), label: addLabel.trim() }),
      });
      if (!resp.ok) throw new Error(await _readError(resp));
      const data = await resp.json();
      setPayload(data);
      setAddIp(""); setAddLabel("");
      toast.success(`Added ${addIp.trim()}`);
    } catch (e) {
      toast.error(e.message || "Could not add IP");
    } finally { setAdding(false); }
  };

  const removeExtra = async (ip) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/egress-ips/extras`, {
        method: "DELETE",
        credentials: "include",
        headers: { ..._authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ip }),
      });
      if (!resp.ok) throw new Error(await _readError(resp));
      const data = await resp.json();
      setPayload(data);
      toast.success(`Removed ${ip}`);
    } catch (e) {
      toast.error(e.message || "Could not remove IP");
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label || `Copied ${text}`);
    } catch {
      toast.error("Could not access the clipboard.");
    }
  };

  const allIps = payload?.all_ips || [];
  const detected = payload?.detected || {};
  const detectedIps = detected?.ips || [];
  const perService = detected?.per_service || [];
  const extras = payload?.extras || [];
  const services = payload?.services || [];
  const cacheAge = detected?.cache_age_s;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminPageHeader
        title="Outbound IP Whitelist"
        subtitle="IPs this backend uses to reach external databases and APIs"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
        icon={<ShieldCheck size={22} weight="duotone" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* ── Intro card ───────────────────────────────────── */}
        <div
          data-testid="egress-intro-card"
          className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-amber-600">
              <Warning size={22} weight="duotone" />
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              <div className="font-semibold text-slate-900 mb-1">
                Production vs preview IPs differ
              </div>
              <p>
                Each environment (preview / production) has its own
                outbound IP. To see what production uses, open this
                page from{" "}
                <code className="bg-slate-100 px-1 py-0.5 rounded-sm">
                  https://compressedairportal.com/admin/whitelist-bootstrap
                </code>{" "}
                — the IPs shown will be the production backend&apos;s.
              </p>
              {payload?.note && (
                <p className="mt-2 text-xs text-slate-500">{payload.note}</p>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div
            data-testid="egress-error"
            className="bg-rose-50 border border-rose-200 text-rose-800 rounded-sm px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        {/* ── All IPs card — the single thing to copy ──────── */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Globe size={18} weight="duotone" />
              <span data-testid="egress-ip-count">
                {allIps.length} {allIps.length === 1 ? "IP" : "IPs"} to whitelist
              </span>
              <span className="ml-1 text-[10.5px] text-slate-400 font-normal">
                · refreshed {_humanAge(cacheAge)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => copyText(allIps.join("\n"), `Copied ${allIps.length} IPs (newline-separated)`)}
                disabled={!allIps.length}
                data-testid="egress-copy-all-btn"
                className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Copy size={14} weight="bold" /> Copy all
              </button>
              <button
                type="button"
                onClick={() => fetchPayload({ force: true })}
                disabled={refreshing}
                data-testid="egress-refresh-btn"
                className="text-xs font-medium px-3 py-1.5 rounded-sm border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
                Re-detect
              </button>
            </div>
          </div>

          {loading && (
            <div className="px-5 py-12 text-center text-sm text-slate-500" data-testid="egress-loading">
              Detecting outbound IPs…
            </div>
          )}

          {!loading && (
            <>
              {/* Combined IP table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="egress-ip-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="px-4 sm:px-5 py-2 font-semibold">IP address</th>
                      <th className="px-4 sm:px-5 py-2 font-semibold">Source</th>
                      <th className="px-4 sm:px-5 py-2 font-semibold">Notes</th>
                      <th className="px-4 sm:px-5 py-2 font-semibold w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allIps.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-400">
                          No IPs detected yet. Click <em>Re-detect</em> to probe the echo services again.
                        </td>
                      </tr>
                    )}
                    {allIps.map((ip, idx) => {
                      const extra = extras.find((e) => (e.ip || "").toLowerCase() === ip.toLowerCase());
                      const auto = detectedIps.includes(ip);
                      return (
                        <tr
                          key={ip}
                          data-testid={`egress-ip-row-${idx}`}
                          className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 sm:px-5 py-3 font-mono text-slate-900 font-semibold text-[13px]">
                            {ip}
                          </td>
                          <td className="px-4 sm:px-5 py-3">
                            {auto ? (
                              <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <Lightning size={10} weight="fill" /> Auto-detected
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold bg-slate-50 text-slate-700 border border-slate-200">
                                <Plus size={10} weight="bold" /> Manual
                              </span>
                            )}
                          </td>
                          <td className="px-4 sm:px-5 py-3 text-slate-500 text-[12.5px]">
                            {extra
                              ? (extra.label || <span className="text-slate-300">—</span>)
                              : <span className="text-slate-300">echo-service consensus</span>}
                          </td>
                          <td className="px-4 sm:px-5 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => copyText(ip, `Copied ${ip}`)}
                                data-testid={`egress-copy-row-${idx}`}
                                className="text-slate-400 hover:text-blue-700 transition-colors p-1"
                                title="Copy IP"
                              >
                                <Copy size={15} weight="bold" />
                              </button>
                              {extra && (
                                <button
                                  type="button"
                                  onClick={() => removeExtra(ip)}
                                  data-testid={`egress-remove-row-${idx}`}
                                  className="text-slate-400 hover:text-rose-700 transition-colors p-1"
                                  title="Remove this manual IP"
                                >
                                  <Trash size={15} weight="bold" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Per-service probe transparency */}
              {perService.length > 0 && (
                <div className="px-4 sm:px-5 py-3 bg-slate-50/60 border-t border-slate-200 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="font-semibold text-slate-600">Echo probes:</span>
                  {perService.map((s) => (
                    <span key={s.name} className="inline-flex items-center gap-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.ok ? "bg-emerald-500" : "bg-rose-400"}`} />
                      {s.name} <span className="text-slate-400">→ {s.ip || "—"}</span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Add extra IP form ────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="mt-0.5 text-slate-500"><Plus size={18} weight="bold" /></div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Add a manual IP</div>
              <p className="text-xs text-slate-500 mt-0.5">
                For NAT egress IPs the echo services don&apos;t return, or for additional IPs the platform team gave you.
              </p>
            </div>
          </div>
          <form onSubmit={addExtra} className="flex flex-col sm:flex-row gap-2" data-testid="egress-add-form">
            <input
              type="text"
              value={addIp}
              onChange={(e) => setAddIp(e.target.value)}
              placeholder="1.2.3.4"
              data-testid="egress-add-ip-input"
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-blue-500 font-mono"
              autoComplete="off"
              spellCheck={false}
            />
            <input
              type="text"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              placeholder="Label (optional) — e.g. Backup NAT egress"
              data-testid="egress-add-label-input"
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-blue-500"
              maxLength={200}
            />
            <button
              type="submit"
              disabled={adding || !addIp.trim()}
              data-testid="egress-add-submit-btn"
              className="px-4 py-2 text-xs font-semibold rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
            >
              <Plus size={14} weight="bold" /> Add IP
            </button>
          </form>
        </div>

        {/* ── External services catalog ────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <Info size={18} weight="duotone" className="text-slate-500" />
            <div className="text-sm font-semibold text-slate-700">
              Where to paste these IPs
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {services.map((svc) => {
              const vs = verifyState[svc.key] || {};
              const result = vs.result;
              return (
                <div
                  key={svc.key}
                  data-testid={`egress-service-${svc.key}`}
                  className="px-4 sm:px-5 py-4 flex items-start gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 text-sm">{svc.name}</div>
                      {svc.critical && (
                        <span className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold bg-rose-50 text-rose-700 border border-rose-200">
                          Required
                        </span>
                      )}
                      {svc.host && (
                        <span className="font-mono text-[11px] text-slate-400">{svc.host}</span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
                      {svc.note}
                    </p>

                    {/* Verify result strip — appears once the user clicks Verify */}
                    {result && (
                      <div
                        data-testid={`egress-verify-result-${svc.key}`}
                        className={
                          "mt-2 inline-flex items-center gap-2 text-[11.5px] px-2.5 py-1 rounded-sm border " +
                          (result.ok
                            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : "bg-rose-50 border-rose-200 text-rose-800")
                        }
                      >
                        {result.ok ? (
                          <CheckCircle size={13} weight="fill" />
                        ) : (
                          <XIcon size={13} weight="bold" />
                        )}
                        <span className="font-semibold">
                          {result.ok ? "Reachable" : "Unreachable"}
                        </span>
                        {result.status && (
                          <span className="font-mono opacity-80">{result.status}</span>
                        )}
                        {typeof result.latency_ms === "number" && (
                          <span className="opacity-70">· {result.latency_ms} ms</span>
                        )}
                        {!result.ok && result.error && (
                          <span className="opacity-80 truncate max-w-[280px]" title={result.error}>
                            · {result.error}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => verifyService(svc.key)}
                      disabled={!!vs.running}
                      data-testid={`egress-service-verify-${svc.key}`}
                      className="text-xs font-medium px-3 py-1.5 rounded-sm border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <ArrowsClockwise
                        size={12}
                        weight="bold"
                        className={vs.running ? "animate-spin" : ""}
                      />
                      {vs.running ? "Verifying…" : "Verify"}
                    </button>
                    {svc.url && (
                      <a
                        href={svc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`egress-service-link-${svc.key}`}
                        className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5 text-slate-700 whitespace-nowrap"
                      >
                        Open <ArrowSquareOut size={12} weight="bold" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Endpoint footnote ────────────────────────────── */}
        <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <CheckCircle size={12} weight="fill" className="text-emerald-500" />
          Endpoint:{" "}
          <code className="bg-slate-100 px-1 py-0.5 rounded-sm font-mono">
            GET /api/admin/egress-ips
          </code>
          <span className="mx-2 text-slate-300">·</span>
          Super-admin only · Cache TTL 5 min
        </div>
      </div>
    </div>
  );
}
